/**
 * Short enquiry form, opened from a card.
 *
 * Each instance stands alone: its own trigger, its own dialog and its own
 * HubSpot form, so an enquiry about one route never arrives looking like an
 * enquiry about another. Where a route has no form GUID of its own it falls
 * back to the shared one and the route is still recorded in the message, so a
 * missing GUID degrades to a labelled enquiry rather than a lost one.
 *
 * A native <dialog> gives the focus trap, Escape handling and page inerting for
 * free, matching the quote builder's popup mode.
 */
class CommercialEnquiry extends HTMLElement {
  constructor() {
    super();
    this.dialog = this.querySelector('[data-enquiry-dialog]');
    this.form = this.querySelector('[data-enquiry-form]');
    this.message = this.querySelector('[data-enquiry-message]');
  }

  connectedCallback() {
    // The dialog is optional: the same component runs inline, which is how the
    // distribution page uses it. Only the form is required.
    if (!this.form) return;
    this.form.addEventListener('submit', this.onSubmit.bind(this));
    if (!this.dialog) return;

    this.addEventListener('click', (event) => {
      if (event.target.closest('[data-enquiry-open]')) {
        event.preventDefault();
        this.open();
      }
      if (event.target.closest('[data-enquiry-close]')) {
        event.preventDefault();
        this.dialog.close();
      }
    });

    // The dialog element is the backdrop; its inner panel is not, so a click
    // landing on the dialog itself came from outside the form.
    this.dialog.addEventListener('click', (event) => {
      if (event.target === this.dialog) this.dialog.close();
    });
    // Chrome 155 was seen firing the dialog's toggle event but never its close
    // event, which left the page scroll-locked. Either one releases it.
    const release = () => { if (!this.dialog.open) document.body.classList.remove('c-modal-open'); };
    this.dialog.addEventListener('close', release);
    this.dialog.addEventListener('toggle', release);
  }

  open() {
    if (!this.dialog.open) this.dialog.showModal();
    document.body.classList.add('c-modal-open');
    this.track('commercial_enquiry_open');
  }

  say(text, ok) {
    if (!this.message) return;
    this.message.textContent = text;
    this.message.hidden = false;
    this.message.classList.toggle('is-error', !ok);
  }

  track(event) {
    const detail = { route: this.dataset.route || '', page_path: window.location.pathname };
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(Object.assign({ event }, detail));
    if (typeof window.gtag === 'function') window.gtag('event', event, detail);
    if (typeof window.clarity === 'function') window.clarity('event', event);
  }

  /**
   * Route first, so whoever picks the enquiry up knows what it is about, then
   * whatever the host put on data-context (the finance calculator writes the
   * figures the buyer was looking at), then what they typed.
   */
  body() {
    const typed = (this.form.querySelector('[name="message"]') || {}).value || '';
    const head = [];
    if (this.dataset.route) head.push(`Route: ${this.dataset.route}`);
    if (this.dataset.context) head.push(this.dataset.context);
    return head.length ? `${head.join('\n')}\n\n${typed}`.trim() : typed.trim();
  }

  async onSubmit(event) {
    event.preventDefault();

    // Radios contribute only when checked; a checkbox group contributes a
    // semicolon-joined list, which is how HubSpot stores a multi-select.
    // A property on the Company record rather than the Contact has to be sent
    // namespaced by object type id, or HubSpot reports its own required
    // company fields as missing and drops the optional ones.
    const objectTypes = {};
    const values = {};
    this.form.querySelectorAll('[name]').forEach((input) => {
      const name = input.name;
      if (input.dataset.object) objectTypes[name] = input.dataset.object;
      if (input.type === 'radio') {
        if (input.checked) values[name] = input.value;
      } else if (input.type === 'checkbox') {
        if (input.checked) values[name] = values[name] ? `${values[name]};${input.value}` : input.value;
      } else {
        values[name] = (input.value || '').trim();
      }
    });

    const missing = [...this.form.querySelectorAll('[required][name]')].find((input) => {
      if (input.type === 'radio' || input.type === 'checkbox') {
        return !this.form.querySelector(`[name="${input.name}"]:checked`);
      }
      return !(input.value || '').trim();
    });
    if (missing) {
      missing.focus();
      return this.say(this.dataset.requiredMessage || 'Fill in the fields marked required.', false);
    }

    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(values.email || '')) {
      return this.say(this.dataset.invalidMessage || 'Check the email address.', false);
    }

    const portal = this.dataset.portalId;
    const guid = this.dataset.formGuid;
    if (!portal || !guid) {
      // Nothing configured yet: say so rather than pretending it worked.
      return this.say(this.dataset.errorMessage || 'This form is not connected yet.', false);
    }

    const fields = Object.keys(values)
      .filter((name) => name !== 'message' && values[name])
      .map((name) => (objectTypes[name]
        ? { objectTypeId: objectTypes[name], name, value: values[name] }
        : { name, value: values[name] }));

    // Only forms that actually carry a message field get one: the route note
    // has nowhere to live otherwise, and HubSpot rejects unknown properties.
    if (this.form.querySelector('[name="message"]')) {
      fields.push({ name: 'message', value: this.body() });
    }

    // Where the lead came from, in the same property the quote builder fills,
    // so an enquiry posted to the quote form is told apart from a full quote.
    if (this.dataset.entryProperty) {
      fields.push({ name: this.dataset.entryProperty, value: this.dataset.entryValue || this.dataset.route || 'Enquiry' });
    }

    const host = this.dataset.region === 'eu1' ? 'api-eu1.hsforms.com' : 'api.hsforms.com';
    const hutk = (document.cookie.match(/(?:^|;\s*)hubspotutk=([^;]*)/) || [])[1];
    const payload = {
      fields,
      context: { pageUri: window.location.href, pageName: document.title },
      legalConsentOptions: {
        consent: {
          consentToProcess: true,
          text: this.dataset.consentText || 'We will use these details to answer this enquiry.',
        },
      },
    };
    if (hutk) payload.context.hutk = hutk;

    try {
      await this.send(`https://${host}/submissions/v3/integration/submit/${portal}/${guid}`, payload);

      this.say(this.dataset.successMessage || 'Thanks, we will be in touch.', true);
      this.form.reset();
      this.track('commercial_enquiry_submit');
    } catch (error) {
      console.error('[commercial-enquiry]', error);
      this.say(this.dataset.errorMessage || 'That did not go through. Try again in a moment.', false);
    }
  }

  /**
   * HubSpot rejects the whole submission when one field is not on the form,
   * which is easy to hit when a route shares another form (the quote form has
   * no message box, say). Those fields are dropped and the lead sent once more,
   * so a form change in HubSpot costs a note rather than the enquiry.
   */
  async send(url, payload, retried = false) {
    const res = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    if (res.ok) return;

    const detail = await res.json().catch(() => ({}));
    const unknown = (detail.errors || [])
      .filter((e) => e.errorType === 'FIELD_NOT_IN_FORM_DEFINITION')
      .map((e) => ((e.message || '').match(/fields\.([\w-]+)/) || [])[1])
      .filter(Boolean);
    const kept = payload.fields.filter((f) => !unknown.includes(f.name));

    if (!retried && unknown.length && kept.length < payload.fields.length) {
      console.warn('[commercial-enquiry] not on the HubSpot form, sent without:', unknown.join(', '));
      return this.send(url, Object.assign({}, payload, { fields: kept }), true);
    }
    throw new Error(`HubSpot responded ${res.status}: ${JSON.stringify(detail.errors || detail)}`);
  }
}

if (!customElements.get('commercial-enquiry')) {
  customElements.define('commercial-enquiry', CommercialEnquiry);
}
